
import React, { useState, useEffect } from 'react';
import { Search, User, CheckCircle, XCircle } from 'lucide-react';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, query, doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { cn } from '@/shared/lib/utils';
import { formatDisplayName, maskString } from '@/shared/utils/formatters';
import { useCryptoPrices } from '@/shared/hooks/useCryptoPrices';

interface UserData {
    id: string;
    userId?: string;
    userName?: string;
    email?: string;
    phone?: string;
    password?: string;
    role?: string;
    status?: 'active' | 'suspended';
    withdrawalSuspended?: boolean;
    tradingSuspended?: boolean;
    approved?: boolean;
    kycLevel?: number;
    createdAt?: any;
    krw?: number;
    btc?: number;
    eth?: number;
    usdt?: number;
    trx?: number;
    bera?: number;
    lastSeen?: any;
    isOnline?: boolean;
    name?: string;
    walletAddress?: string; // Legacy
    walletAddressBTC?: string;
    walletAddressETH?: string;
    walletAddressUSDT?: string;
    walletAddressBERA?: string;
    lastLoginIP?: string;
    lastLoginTime?: any;
    btcCostBasis?: number;
    ethCostBasis?: number;
    usdtCostBasis?: number;
    trxCostBasis?: number;
    beraCostBasis?: number;
}

const AdminUsersPage: React.FC = () => {
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
    const { prices: livePrices } = useCryptoPrices();

    // Push Message State
    const [showPushModal, setShowPushModal] = useState(false);
    const [pushUser, setPushUser] = useState<UserData | null>(null);
    const [pushTitle, setPushTitle] = useState('시스템 알림');
    const [pushMessage, setPushMessage] = useState('');
    const [isSendingPush, setIsSendingPush] = useState(false);

    // Asset Management State
    const [assetType, setAssetType] = useState('krw');
    const [amount, setAmount] = useState('');
    const [action, setAction] = useState<'add' | 'subtract'>('add');

    // User Edit State
    const [editingUser, setEditingUser] = useState<UserData | null>(null);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editWalletAddressBTC, setEditWalletAddressBTC] = useState('');
    const [editWalletAddressETH, setEditWalletAddressETH] = useState('');
    const [editWalletAddressUSDT, setEditWalletAddressUSDT] = useState('');
    const [editWalletAddressBERA, setEditWalletAddressBERA] = useState('');
    const [unitPrice, setUnitPrice] = useState(''); // 지급 단가 (원/개) - 모든 코인 공통
    const [costBasisEdit, setCostBasisEdit] = useState(''); // 평단가 직접 수정용

    // Batch Import State
    const [showImportModal, setShowImportModal] = useState(false);
    const [importEmails, setImportEmails] = useState('');
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        const q = query(collection(db, 'users'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const userData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as UserData[];
            setUsers(userData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const toggleUserStatus = async (userId: string, currentStatus?: string) => {
        const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
        try {
            await updateDoc(doc(db, 'users', userId), {
                status: newStatus
            });
        } catch (error) {
            console.error("Failed to update user status", error);
            alert("상태 변경에 실패했습니다.");
        }
    };

    const toggleUserApproval = async (userId: string, currentApproval?: boolean) => {
        const newApproval = !currentApproval;
        try {
            await updateDoc(doc(db, 'users', userId), {
                approved: newApproval
            });
            alert(newApproval ? '사용자가 승인되었습니다.' : '사용자 승인이 취소되었습니다.');
        } catch (error) {
            console.error("Failed to toggle user approval", error);
            alert("승인 상태 변경에 실패했습니다.");
        }
    };

    const toggleWithdrawalSuspension = async (userId: string, currentStatus?: boolean) => {
        const newStatus = !currentStatus;
        try {
            await updateDoc(doc(db, 'users', userId), {
                withdrawalSuspended: newStatus
            });
            alert(newStatus ? '출금이 정지되었습니다.' : '출금 정지가 해제되었습니다.');
        } catch (error) {
            console.error("Failed to toggle withdrawal suspension", error);
            alert("출금정지 상태 변경에 실패했습니다.");
        }
    };

    const toggleTradingSuspension = async (userId: string, currentStatus?: boolean) => {
        const newStatus = !currentStatus;
        try {
            await updateDoc(doc(db, 'users', userId), {
                tradingSuspended: newStatus
            });
            alert(newStatus ? '거래가 정지되었습니다.' : '거래 정지가 해제되었습니다.');
        } catch (error) {
            console.error("Failed to toggle trading suspension", error);
            alert("거래정지 상태 변경에 실패했습니다.");
        }
    };

    const handleAssetUpdate = async () => {
        if (!selectedUser || !amount) return;
        const val = parseFloat(amount.replace(/,/g, ''));
        if (isNaN(val) || val <= 0) {
            alert('유효한 금액을 입력해주세요.');
            return;
        }

        if (action === 'subtract') {
            const currentBalance = (selectedUser[assetType as keyof UserData] as number) || 0;
            if (currentBalance < val) {
                alert(`잔액이 부족합니다. (현재 보유: ${currentBalance.toLocaleString()} ${assetType.toUpperCase()})`);
                return;
            }
        }

        const finalAmount = action === 'add' ? val : -val;

        // 코인 호류별 costBasis 필드 매핑
        const costBasisField: Record<string, string> = {
            btc: 'btcCostBasis',
            eth: 'ethCostBasis',
            usdt: 'usdtCostBasis',
            trx: 'trxCostBasis',
            bera: 'beraCostBasis'
        };

        try {
            const { increment, serverTimestamp, addDoc, collection } = await import('firebase/firestore');

            const updateData: Record<string, any> = { [assetType]: increment(finalAmount) };

            // 코인 지급 시 가중평균 costBasis 계산
            // 단가 미입력 시 현재 실시간 시세를 자동으로 평단가로 사용
            if (action === 'add' && costBasisField[assetType]) {
                const up = unitPrice ? parseFloat(unitPrice) : getExchangeRate(assetType);
                if (!isNaN(up) && up > 0) {
                    const currentQty = (selectedUser[assetType as keyof UserData] as number) || 0;
                    const currentCB = (selectedUser[costBasisField[assetType] as keyof UserData] as number) || 0;
                    const totalQty = currentQty + val;
                    const newCB = totalQty > 0 ? ((currentQty * currentCB) + (val * up)) / totalQty : up;
                    updateData[costBasisField[assetType]] = Math.round(newCB);
                }
            }

            await updateDoc(doc(db, 'users', selectedUser.id), updateData);

            await addDoc(collection(db, 'transactions'), {
                userId: selectedUser.id,
                type: action === 'add' ? 'DEPOSIT' : 'WITHDRAW',
                amount: val,
                currency: assetType.toUpperCase(),
                ...(action === 'add' ? { unitPrice: unitPrice ? parseFloat(unitPrice) : getExchangeRate(assetType) } : {}),
                status: 'COMPLETED',
                description: `관리자 ${action === 'add' ? '지급' : '회수'}`,
                createdAt: serverTimestamp(),
                approvedAt: serverTimestamp()
            });

            alert('처리가 완료되었습니다.');
            setSelectedUser(null);
            setAmount('');
            setUnitPrice('');
        } catch (error) {
            console.error("Asset update failed", error);
            alert('처리 중 오류가 발생했습니다.');
        }
    };

    // 평단가 직접 수정 핸들러
    const handleUpdateCostBasis = async () => {
        if (!selectedUser || !assetType || assetType === 'krw') return;
        const cbVal = parseFloat(costBasisEdit);
        if (isNaN(cbVal) || cbVal <= 0) {
            alert('유효한 평단가를 입력해주세요.');
            return;
        }
        const costBasisField: Record<string, string> = {
            btc: 'btcCostBasis',
            eth: 'ethCostBasis',
            usdt: 'usdtCostBasis',
            trx: 'trxCostBasis',
            bera: 'beraCostBasis'
        };
        if (!costBasisField[assetType]) return;
        try {
            await updateDoc(doc(db, 'users', selectedUser.id), {
                [costBasisField[assetType]]: Math.round(cbVal)
            });
            alert('평단가가 수정되었습니다.');
            setCostBasisEdit('');
        } catch (error) {
            console.error('평단가 수정 실패', error);
            alert('평단가 수정 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteUser = async (userId: string, userName?: string) => {
        if (!window.confirm(`정말로 ${userName || userId} 사용자를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
            return;
        }

        try {
            const { deleteDoc } = await import('firebase/firestore');

            // 1. Firebase Auth 계정 삭제 (Cloud Function 호출)
            try {
                const functions = getFunctions();
                const deleteAuthUser = httpsCallable(functions, 'deleteUserAccount');
                await deleteAuthUser({ uid: userId });
            } catch (authErr) {
                console.warn('[Admin] Auth 삭제 실패 (계속 진행):', authErr);
            }

            // 2. Firestore 문서 삭제
            await deleteDoc(doc(db, 'users', userId));
            alert('사용자 및 로그인 권한이 완전히 삭제되었습니다.');
        } catch (error) {
            console.error("Failed to delete user", error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const handleSendPush = async () => {
        if (!pushUser || !pushMessage.trim()) {
            alert("메시지를 입력해주세요.");
            return;
        }

        setIsSendingPush(true);
        try {
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
            await addDoc(collection(db, 'user_push_notifications'), {
                userId: pushUser.id,
                title: pushTitle,
                message: pushMessage,
                read: false,
                createdAt: serverTimestamp()
            });

            alert("푸시 메시지가 전송되었습니다.");
            setShowPushModal(false);
            setPushMessage('');
            setPushTitle('시스템 알림');
        } catch (error) {
            console.error("Failed to send push message", error);
            alert("메시지 전송에 실패했습니다.");
        } finally {
            setIsSendingPush(false);
        }
    };

    const handleUpdateUserInfo = async () => {
        if (!editingUser) return;

        try {
            await updateDoc(doc(db, 'users', editingUser.id), {
                name: editName,
                email: editEmail,
                phone: editPhone,
                password: editPassword,
                walletAddressBTC: editWalletAddressBTC,
                walletAddressETH: editWalletAddressETH,
                walletAddressUSDT: editWalletAddressUSDT,
                walletAddressBERA: editWalletAddressBERA
            });
            alert('사용자 정보가 수정되었습니다.');
            setEditingUser(null);
            setEditName('');
            setEditEmail('');
            setEditPhone('');
            setEditPassword('');
            setEditWalletAddressBTC('');
            setEditWalletAddressETH('');
            setEditWalletAddressUSDT('');
            setEditWalletAddressBERA('');
        } catch (error) {
            console.error("Failed to update user info", error);
            alert('수정 중 오류가 발생했습니다.');
        }
    };

    const handleResetAllAssets = async () => {
        if (!window.confirm("⚠️ 경고: 모든 사용자의 자산이 0으로 초기화됩니다.\n이 작업은 되돌릴 수 없습니다.\n정말 진행하시겠습니까?")) {
            return;
        }

        const confirmPhrase = "초기화";
        const userInput = prompt(`진행하려면 "${confirmPhrase}"를 입력하세요.`);
        if (userInput !== confirmPhrase) {
            alert("입력값이 일치하지 않아 취소되었습니다.");
            return;
        }

        setLoading(true);
        try {
            const { writeBatch } = await import('firebase/firestore');
            const batch = writeBatch(db);

            // Note: Batch limit is 500. If users > 500, need multiple batches.
            // For now assuming < 500 for this immediate fix.
            users.forEach(user => {
                const userRef = doc(db, 'users', user.id);
                batch.update(userRef, {
                    krw: 0,
                    btc: 0,
                    eth: 0,
                    usdt: 0,
                    trx: 0,
                    sol: 0
                });
            });

            await batch.commit();
            alert("모든 사용자의 자산이 0으로 초기화되었습니다.");
        } catch (error) {
            console.error("Failed to reset assets", error);
            alert("초기화 중 오류가 발생했습니다: " + (error as any).message);
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (user: UserData) => {
        setEditingUser(user);
        setEditName(user.name || user.userName || '');
        setEditEmail(user.email || '');
        setEditPhone(user.phone || '');
        setEditPassword(user.password || '');
        // Fallback to legacy or defaults if missing
        setEditWalletAddressBTC(user.walletAddressBTC || user.walletAddress || 'bc1qth8rh8ygp2s9durnjlxc0knhhp0fu8wk7z6wse');
        setEditWalletAddressETH(user.walletAddressETH || '0x3dFbE08Cc996fa279aB9778D554C099FF0178Ee9');
        setEditWalletAddressUSDT(user.walletAddressUSDT || 'TDDQa549R9cVDdRv6TbbJAMzeBuba4Bop1');
        setEditWalletAddressBERA(user.walletAddressBERA || '');
    };

    const handleBatchImport = async () => {
        if (!importEmails.trim()) {
            alert("이메일 리스트를 입력해주세요.");
            return;
        }

        const emailList = importEmails
            .split(/[\n,;]/)
            .map(e => e.trim())
            .filter(e => e.includes('@'));

        if (emailList.length === 0) {
            alert("유효한 이메일 형식이 없습니다.");
            return;
        }

        if (!window.confirm(`${emailList.length}개의 계정을 일괄 생성하시겠습니까?`)) return;

        setIsImporting(true);
        try {
            const functions = getFunctions();
            const batchCreateUsers = httpsCallable(functions, 'batchCreateUsers');
            const result = await batchCreateUsers({ emails: emailList }) as any;

            if (result.data.success) {
                alert(`가입 완료: ${result.data.processed}개 / 실패: ${result.data.failed}개\n초기 비밀번호: cc123456!`);
                setImportEmails('');
                setShowImportModal(false);
            }
        } catch (error: any) {
            console.error("Batch import failed", error);
            alert("오류 발생: " + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const filteredUsers = users.filter(user =>
        (user.userName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    const formatMoney = (value: number | undefined) => {
        if (value === undefined || value === null) return '0';
        return value.toLocaleString();
    };

    const isOnline = (lastSeen: any) => {
        if (!lastSeen) return false;
        const now = new Date().getTime();
        const seen = lastSeen.toDate ? lastSeen.toDate().getTime() : new Date(lastSeen).getTime();
        return (now - seen) < 2 * 60 * 1000; // 2 minutes
    };

    // 실시간 시세 기반 환율
    const getExchangeRate = (currency: string) => {
        switch (currency) {
            case 'btc': return livePrices?.BTC?.price || 136_000_000;
            case 'eth': return livePrices?.ETH?.price || 3_600_000;
            case 'usdt': return livePrices?.USDT?.price || 1_442;
            case 'bera': return livePrices?.BERA?.price || 7_000;
            case 'trx': return 230;
            default: return 1;
        }
    };

    const estimatedKRW = assetType !== 'krw' && amount ? parseFloat(amount) * getExchangeRate(assetType) : 0;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">사용자 관리</h1>
                    <p className="text-slate-500 mt-1">총 {filteredUsers.length}명의 사용자가 등록되어 있습니다.</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleResetAllAssets}
                        className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-xs font-bold hover:bg-red-200 transition-colors border border-red-200"
                    >
                        ⚠️ 전체 자산 초기화
                    </button>
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 flex items-center gap-2"
                    >
                        <User className="w-3.5 h-3.5" />
                        일괄 고객 임포트 (복구)
                    </button>
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="이름, 이메일, UID 검색"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 h-10 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">사용자 정보</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">접속 상태</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">보유 자산 (KRW)</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">KYC 레벨</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">승인 상태</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">출금 상태</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">거래 상태</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">관리</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-20 text-center text-slate-500">데이터를 불러오는 중입니다...</td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-20 text-center text-slate-500">검색 결과가 없습니다.</td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => {
                                    const online = isOnline(user.lastSeen);
                                    return (
                                        <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 relative">
                                                        <User className="h-5 w-5 text-slate-400" />
                                                        {online && <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-slate-900">{formatDisplayName(user)}</p>
                                                        <p className="text-xs text-slate-500">{user.email || maskString(user.id)}</p>
                                                        <p className="text-xs text-slate-500 mt-0.5">Phone: {user.phone || '-'}</p>
                                                        <p className="text-xs text-slate-400 mt-0.5 font-mono bg-slate-100/50 inline-block px-1 rounded">Pwd: {user.password || '-'}</p>
                                                        {user.lastLoginIP && (
                                                            <p className="text-xs text-blue-600 font-mono mt-0.5">IP: {user.lastLoginIP}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {online ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                        접속중
                                                    </span>
                                                ) : (
                                                    <div className="text-xs text-slate-400">
                                                        <p>오프라인</p>
                                                        <p className="text-[10px]">{user.lastSeen?.toDate ? user.lastSeen.toDate().toLocaleString() : '-'}</p>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="font-bold text-slate-700">₩ {formatMoney(user.krw)}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                                    레벨 {user.kycLevel || 1}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={cn(
                                                    "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium",
                                                    user.approved === false
                                                        ? "bg-yellow-50 text-yellow-700 border border-yellow-100"
                                                        : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                                )}>
                                                    {user.approved === false ? '승인 대기' : '승인됨'}
                                                </span>
                                            </td>
                                            {/* 계정 상태 컬럼 제거 - 정지 버튼 삭제에 따른 정리 */}
                                            <td className="px-6 py-4">
                                                <span className={cn(
                                                    "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium",
                                                    user.withdrawalSuspended
                                                        ? "bg-orange-50 text-orange-700 border border-orange-100"
                                                        : "bg-slate-50 text-slate-700 border border-slate-100"
                                                )}>
                                                    {user.withdrawalSuspended ? '정지' : '정상'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={cn(
                                                    "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium",
                                                    user.tradingSuspended
                                                        ? "bg-orange-50 text-orange-700 border border-orange-100"
                                                        : "bg-slate-50 text-slate-700 border border-slate-100"
                                                )}>
                                                    {user.tradingSuspended ? '정지' : '정상'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => openEditModal(user)}
                                                        className="px-3 py-1.5 rounded-md text-xs font-bold bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors border border-slate-200"
                                                    >
                                                        정보 수정
                                                    </button>
                                                    <button
                                                        onClick={() => setSelectedUser(user)}
                                                        className="px-3 py-1.5 rounded-md text-xs font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors border border-blue-200"
                                                    >
                                                        자산 관리
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setPushUser(user);
                                                            setShowPushModal(true);
                                                        }}
                                                        className="px-3 py-1.5 rounded-md text-xs font-bold bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors border border-amber-200"
                                                    >
                                                        푸시메세지
                                                    </button>
                                                    {/* 정지 버튼 제거: 거래정지와 중복 기능 */}
                                                    <button
                                                        onClick={() => toggleUserApproval(user.id, user.approved)}
                                                        className={cn(
                                                            "px-3 py-1.5 rounded-md text-xs font-bold transition-all border shadow-sm",
                                                            user.approved === false
                                                                ? "bg-white border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300"
                                                                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                                                        )}
                                                    >
                                                        {user.approved === false ? '승인' : '승인취소'}
                                                    </button>
                                                    <button
                                                        onClick={() => toggleWithdrawalSuspension(user.id, user.withdrawalSuspended)}
                                                        className={cn(
                                                            "px-3 py-1.5 rounded-md text-xs font-bold transition-all border shadow-sm",
                                                            user.withdrawalSuspended
                                                                ? "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                                                : "bg-white border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300"
                                                        )}
                                                    >
                                                        {user.withdrawalSuspended ? '출금허용' : '출금정지'}
                                                    </button>
                                                    <button
                                                        onClick={() => toggleTradingSuspension(user.id, user.tradingSuspended)}
                                                        className={cn(
                                                            "px-3 py-1.5 rounded-md text-xs font-bold transition-all border shadow-sm",
                                                            user.tradingSuspended
                                                                ? "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                                                : "bg-white border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300"
                                                        )}
                                                    >
                                                        {user.tradingSuspended ? '거래허용' : '거래정지'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user.id, user.userName)}
                                                        className="px-3 py-1.5 rounded-md text-xs font-bold bg-white text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 border border-transparent transition-all"
                                                        title="계정 삭제"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                        Showing {filteredUsers.length} users
                    </span>
                </div>
            </div>

            {/* Asset Management Modal */}
            {selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <h3 className="text-lg font-bold text-slate-900">자산 관리</h3>
                            <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-sm font-bold text-slate-900">{formatDisplayName(selectedUser)} ({selectedUser.email || 'No Email'})</p>
                                <p className="text-xs text-slate-500 mt-1 font-mono">{maskString(selectedUser.id)}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
                                <button
                                    onClick={() => setAction('add')}
                                    className={cn(
                                        "py-2 rounded-lg text-sm font-bold transition-all",
                                        action === 'add' ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                                    )}
                                >
                                    지급 (입금)
                                </button>
                                <button
                                    onClick={() => setAction('subtract')}
                                    className={cn(
                                        "py-2 rounded-lg text-sm font-bold transition-all",
                                        action === 'subtract' ? "bg-white text-red-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                                    )}
                                >
                                    회수 (출금)
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">자산 종류</label>
                                    <select
                                        value={assetType}
                                        onChange={(e) => setAssetType(e.target.value)}
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    >
                                        <option value="krw">KRW (원화)</option>
                                        <option value="btc">BTC (비트코인)</option>
                                        <option value="eth">ETH (이더리움)</option>
                                        <option value="usdt">USDT (테더)</option>
                                        <option value="bera">🐻 BERA (베라코인)</option>
                                    </select>
                                </div>

                                {/* 코인 단가 설정 (KRW 제외, 지급 시만 표시) */}
                                {assetType !== 'krw' && action === 'add' && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-amber-600">💰 지급 단가 (원/개) — 입력 시 평단가 자동 계산</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                value={unitPrice}
                                                onChange={(e) => setUnitPrice(e.target.value)}
                                                className="w-full h-12 bg-amber-50 border border-amber-300 rounded-xl px-4 pr-12 text-sm font-bold outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-400"
                                                placeholder={`미입력 시 현재 시세(₩${getExchangeRate(assetType).toLocaleString()}) 자동 적용`}
                                                min="1"
                                            />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-amber-600">원</span>
                                        </div>
                                        {unitPrice && (
                                            <p className="text-[11px] text-amber-600 font-bold bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                                                총 지급가치: {amount || '0'} {assetType.toUpperCase()} × {unitPrice}원 = ₩{((parseFloat(amount || '0') || 0) * (parseFloat(unitPrice) || 0)).toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">수량 / 금액</label>
                                    <input
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="0.00000000"
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        step="any"
                                    />
                                    {assetType !== 'krw' && amount && (
                                        <p className="text-xs text-blue-600 font-bold mt-1">
                                            예상 가치: ₩{estimatedKRW.toLocaleString()}
                                        </p>
                                    )}
                                    <p className="text-xs text-right text-slate-400 mt-1">
                                        현재 보유: {selectedUser[assetType as keyof UserData]?.toLocaleString() || 0} {assetType.toUpperCase()}
                                    </p>
                                </div>

                                {/* 평단가 직접 수정 섹션 (KRW 제외) */}
                                {assetType !== 'krw' && (
                                    <div className="pt-2 border-t border-slate-100 space-y-2">
                                        <label className="text-xs font-bold text-violet-600">✏️ 평단가 직접 수정 (원/개)</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="number"
                                                value={costBasisEdit}
                                                onChange={(e) => setCostBasisEdit(e.target.value)}
                                                placeholder={`현재 평단가: ₩${((selectedUser[{ btc: 'btcCostBasis', eth: 'ethCostBasis', usdt: 'usdtCostBasis', trx: 'trxCostBasis', bera: 'beraCostBasis' }[assetType] as keyof UserData] as number) || 0).toLocaleString()}`}
                                                className="flex-1 h-10 bg-violet-50 border border-violet-300 rounded-xl px-3 text-sm font-bold outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-400"
                                                min="1"
                                            />
                                            <button
                                                onClick={handleUpdateCostBasis}
                                                className="px-4 h-10 rounded-xl bg-violet-600 text-white text-xs font-black hover:bg-violet-500 transition-colors whitespace-nowrap"
                                            >
                                                수정
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleAssetUpdate}
                                className={cn(
                                    "w-full h-14 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-[0.98]",
                                    action === 'add'
                                        ? "bg-blue-600 hover:bg-blue-500 shadow-blue-500/20"
                                        : "bg-red-600 hover:bg-red-500 shadow-red-500/20"
                                )}
                            >
                                {action === 'add' ? '지급하기' : '회수하기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <h3 className="text-lg font-bold text-slate-900">사용자 정보 수정</h3>
                            <button onClick={() => setEditingUser(null)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-sm font-bold text-slate-900">{formatDisplayName(editingUser)} ({editingUser.email || 'No Email'})</p>
                                <p className="text-xs text-slate-500 mt-1 font-mono">{maskString(editingUser.id)}</p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">이름 (Name)</label>
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        placeholder="이름"
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">이메일 (Email)</label>
                                    <input
                                        type="email"
                                        value={editEmail}
                                        onChange={(e) => setEditEmail(e.target.value)}
                                        placeholder="user@example.com"
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">휴대폰 번호 (Phone)</label>
                                    <input
                                        type="tel"
                                        value={editPhone}
                                        onChange={(e) => setEditPhone(e.target.value)}
                                        placeholder="010-0000-0000"
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">비밀번호 (Password)</label>
                                    <input
                                        type="text"
                                        value={editPassword}
                                        onChange={(e) => setEditPassword(e.target.value)}
                                        placeholder="비밀번호"
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="border-t border-slate-100 my-4 pt-4">
                                    <p className="text-xs font-bold text-slate-400 mb-4 uppercase tracking-wider">가상자산 지갑 주소</p>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500">Bitcoin (BTC) Address</label>
                                            <input
                                                type="text"
                                                value={editWalletAddressBTC}
                                                onChange={(e) => setEditWalletAddressBTC(e.target.value)}
                                                placeholder="bc1q..."
                                                className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500">Ethereum (ETH) Address</label>
                                            <input
                                                type="text"
                                                value={editWalletAddressETH}
                                                onChange={(e) => setEditWalletAddressETH(e.target.value)}
                                                placeholder="0x..."
                                                className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500">Tether (USDT) Address</label>
                                            <input
                                                type="text"
                                                value={editWalletAddressUSDT}
                                                onChange={(e) => setEditWalletAddressUSDT(e.target.value)}
                                                placeholder="T..."
                                                className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500">Berachain (BERA) Address</label>
                                            <input
                                                type="text"
                                                value={editWalletAddressBERA}
                                                onChange={(e) => setEditWalletAddressBERA(e.target.value)}
                                                placeholder="0x..."
                                                className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 font-mono"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleUpdateUserInfo}
                                className="w-full h-14 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-[0.98] bg-blue-600 hover:bg-blue-500 shadow-blue-500/20"
                            >
                                수정사항 저장
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Push Message Modal */}
            {showPushModal && pushUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-amber-50/50">
                            <h3 className="text-lg font-bold text-amber-900">푸시 메시지 전송</h3>
                            <button onClick={() => setShowPushModal(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-sm font-bold text-slate-900">{formatDisplayName(pushUser)} ({pushUser.email || 'No Email'})</p>
                                <p className="text-xs text-slate-500 mt-1 font-mono">{maskString(pushUser.id)}</p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">알림 제목</label>
                                    <input
                                        type="text"
                                        value={pushTitle}
                                        onChange={(e) => setPushTitle(e.target.value)}
                                        placeholder="알림 제목"
                                        className="w-full h-12 bg-white border border-slate-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500">메시지 내용</label>
                                    <textarea
                                        value={pushMessage}
                                        onChange={(e) => setPushMessage(e.target.value)}
                                        placeholder="사용자에게 실시간으로 전달할 메시지를 입력하세요."
                                        className="w-full h-32 bg-white border border-slate-200 rounded-xl p-4 text-sm font-bold outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none"
                                    ></textarea>
                                </div>
                            </div>

                            <button
                                onClick={handleSendPush}
                                disabled={isSendingPush || !pushMessage.trim()}
                                className={cn(
                                    "w-full h-14 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2",
                                    isSendingPush ? "bg-slate-400 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-500 shadow-amber-500/20"
                                )}
                            >
                                {isSendingPush ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        전송 중...
                                    </>
                                ) : (
                                    <>메시지 전송하기</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Batch Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-indigo-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-indigo-900">일괄 고객 임포트 (데이터 복구)</h3>
                                <p className="text-xs text-indigo-500 mt-0.5">이전 프로젝트의 고객 이메일 리스트를 붙여넣으세요.</p>
                            </div>
                            <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 mb-4">
                                <p className="text-[11px] text-amber-700 leading-relaxed">
                                    • 줄바꿈, 콤마(,), 세미콜론(;)으로 이메일을 구분합니다.<br />
                                    • 중복된 이메일은 자동으로 제외되거나 업데이트됩니다.<br />
                                    • 초기 비밀번호는 <span className="font-bold">cc123456!</span> 로 일괄 설정됩니다.
                                </p>
                            </div>
                            <textarea
                                value={importEmails}
                                onChange={(e) => setImportEmails(e.target.value)}
                                placeholder="example1@email.com&#10;example2@email.com"
                                className="w-full h-64 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none font-mono"
                            ></textarea>
                            <button
                                onClick={handleBatchImport}
                                disabled={isImporting || !importEmails.trim()}
                                className={cn(
                                    "w-full h-14 rounded-xl text-white font-bold text-sm shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2",
                                    isImporting ? "bg-slate-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20"
                                )}
                            >
                                {isImporting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        처리 중...
                                    </>
                                ) : (
                                    <>계정 일괄 생성 실행</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsersPage;
